import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Typography, Unstable_Grid2 as Grid} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DefaultPaginationInfo} from '../../../../../../../core/constants/enums';
import {BizPointAdminLiabilityDataGridColumnNames} from '../../../../../../../core/constants/table/data-grid-column-keys';
import PartnerDataGrid from '../../../../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from '../../../../../../../packages/data-grid';
import useSearch from '../../../../../../hooks/use-search';
import useIsMounted from '../../../../../../hooks/use-is-mounted';
import {useConfigureNotebook} from '../../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../../hooks/use-behavioural-analytics-tool';
import useAccessLevels from '../../../../../../hooks/use-access-levels';
import SearchInput from '../../../../../../components/base/input/search_input';
import PageHeader from '../../components/page-header';
import StatCard from '../../components/stat-card';
import {paginateMockItems} from '../../mock-search';
import {mockLiability} from '../../mock-data';

const dataGridSaveConfig = {name: 'BizPointAdminLiabilityDataGrid', version: 1};
const col = BizPointAdminLiabilityDataGridColumnNames;

const searchOptions = {
    keywordFields: ['business'],
    sortFieldMap: {
        [col.business]: 'business',
        [col.outstanding]: 'outstanding',
        [col.undistributed]: 'undistributed',
        [col.inFreeze]: 'inFreeze',
    },
};

const AdminLiabilityReportView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.admin.liabilityReport'});
    const {t: tSearch} = useTranslation('views', {keyPrefix: 'app-specific.biz-point.common.search'});
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportAdminLiabilityReport = operatorAccessLevels.packages.bizPoint.exportAdminLiabilityReport;
    const {configureNotebookTags} = useConfigureNotebook('biz-point-admin-liability-report');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const [data, setData] = useState([]);

    /**
     * Configures notebook tags and behavioral analytics for liability report.
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.bizPointAdminLiabilityReport);
        configureBehavioralTool((tags) => tags.bizPointAdminLiabilityReport);
    }, [configureBehavioralTool, configureNotebookTags]);

    const columns = useMemo(() => [
        {title: t('business'), name: col.business, type: DataGridColumnTypes.string, alignment: DataGridColumnAlignments.left},
        {title: t('outstanding'), name: col.outstanding, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('undistributed'), name: col.undistributed, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
        {title: t('inFreeze'), name: col.inFreeze, type: DataGridColumnTypes.number, alignment: DataGridColumnAlignments.right},
    ], [t]);

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(
        () => (dataGridPageSize ? {...DefaultPaginationInfo, pageSize: dataGridPageSize} : DefaultPaginationInfo),
        [dataGridPageSize],
    );

    /**
     * Loads per-business liability breakdown from mock data.
     */
    const search = useCallback(async (args) => {
        await Promise.resolve();
        // TODO: Replace with real API when endpoint is available
        const page = paginateMockItems(mockLiability.byBusiness, args, searchOptions);
        if (!isMounted()) {
            return;
        }
        setData(page);
    }, [isMounted]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        filters: {keyword: ''},
    }, true);

    /**
     * Applies keyword filter via useSearch (panel SearchInput pattern).
     */
    const searchByKeyword = useCallback((keyword) => {
        searchData.setFilters({
            keyword: keyword?.toString()?.trim() ?? '',
        });
    }, [searchData]);

    const getExportInfo = useCallback(async () => {
        const items = await Promise.resolve(paginateMockItems(mockLiability.byBusiness, {
            filters: searchData.filters,
            orderBy: searchData.orderBy,
            paginationInfo: {currentPage: 1, pageSize: 100000},
            setPaginationInfo: () => {},
        }, searchOptions));
        if (!items?.length) {
            return undefined;
        }
        const excludeNames = ['actions'];
        const valueResolvers = {};
        const exportColumns = columns.filter((column) => {
            if (!column?.name || excludeNames.includes(column.name)) {
                return false;
            }
            if (column.type === DataGridColumnTypes.element && !valueResolvers[column.name]) {
                return false;
            }
            return true;
        });
        const body = items.map((item) => {
            const row = {};
            for (const column of exportColumns) {
                const header = typeof column.title === 'function'
                    ? column.title()?.toString()
                    : (column.title ?? column.name);
                if (valueResolvers[column.name]) {
                    row[header] = valueResolvers[column.name](item) ?? '--';
                } else {
                    const value = item[column.name];
                    row[header] = value == null || value === '' ? '--' : String(value);
                }
            }
            return row;
        });
        return {title: t('exportTitle'), body};
    }, [columns, searchData.filters, searchData.orderBy, t]);

    const rows = useMemo(() => data.map((row) => ({
        key: row.business,
        data: row,
        cells: {
            [col.business]: row.business,
            [col.outstanding]: row.outstanding.toLocaleString(),
            [col.undistributed]: row.undistributed.toLocaleString(),
            [col.inFreeze]: row.inFreeze.toLocaleString(),
        },
    })), [data]);

    return (
        <Box>
            <PageHeader title={t('title')}/>

            <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1.5}}>
                {t('summary')}
            </Typography>
            <Grid container spacing={1} sx={{justifyContent: 'space-evenly', mb: 3}}>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('outstandingCustomerBizCad')}
                        value={t('bizCompact', {
                            value: (mockLiability.outstandingCustomerBizCad / 1_000_000).toFixed(2).replace(/\.?0+$/, '') + 'M',
                        })}
                        valueAdornment="CAD"
                        note={t('cadLiabilityNote', {
                            dollars: mockLiability.outstandingCadLiabilityDollars.toLocaleString(),
                        })}
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('outstandingCustomerBizUsd')}
                        value={t('bizCompact', {
                            value: `${Math.round(mockLiability.outstandingCustomerBizUsd / 1000)}K`,
                        })}
                        valueAdornment="USD"
                        note={t('usdLiabilityNote', {
                            dollars: mockLiability.outstandingUsdLiabilityDollars.toLocaleString(),
                        })}
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('undistributedBiz')}
                        value={t('bizCompact', {
                            value: `${Math.round(mockLiability.undistributedBiz / 1000)}K`,
                        })}
                        note={t('undistributedNote')}
                    />
                </Grid>
                <Grid xs={12} sm={6} md={3}>
                    <StatCard
                        label={t('inFreezeBiz')}
                        value={t('bizCompact', {
                            value: `${Math.round(mockLiability.inFreezeBiz / 1000)}K`,
                        })}
                        note={t('inFreezeNote')}
                    />
                </Grid>
            </Grid>

            <Box sx={{mb: 3}}>
                <SearchInput
                    loading={searchData.loading}
                    autoSearch
                    immediateSearch
                    search={searchByKeyword}
                    initialValue={searchData.filters?.keyword ?? undefined}
                    label={tSearch('keyword')}
                />
            </Box>

            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{columns}}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                onSortByChanged={(_, current) => searchData.setOrderBy?.(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo?.((prev) => ({...prev, ...current}))}
                getExportInfo={canExportAdminLiabilityReport ? getExportInfo : undefined}
            />
        </Box>
    );
};

export default AdminLiabilityReportView;
